import * as R from 'ramda';
import memoize from 'memoize-one';
import {
  getRequestArgs,
  getDataquery as getDataqueryMicrodata,
} from '@sis-cc/dotstatsuite-sdmxjs';
import { rules2 } from '@sis-cc/dotstatsuite-components';
import {
  getMicrodataConstraints,
  getViewer,
  getDisplay,
  getDataflow,
  getLocale,
} from './router';
import { getCustomAttributes } from './index';
import {
  getRawStructureRequestArgs,
  getDataRequestParams,
  getExternalReference,
  getDimensions,
  getTimePeriodArtefact,
  getDataflowName,
  getAnnotations,
  getHierarchies,
} from './sdmx';
import { getDescendants, getDescendantsFromHCL } from '../lib/sdmx/microdata';
import {
  dataFileRequestArgsWrapper,
  rawDataRequestArgsWrapper,
} from '../lib/sdmx';
import { getDefaultCombinations, dataRequestSize } from '../lib/settings';
import { MICRODATA } from '../utils/constants';

const getState = R.prop('microdata');

export const getDimensionId = memoize((state) =>
  R.prop('dimensionId', getState(state)),
);
export const getData = memoize((state) => R.prop('data', getState(state)));
export const getHasMicrodata = memoize(
  (state) => !R.isNil(getDimensionId(state)),
);
export const getHasMicrodataData = memoize((state) => !R.isNil(getData(state)));
export const getIsMicrodata = memoize((state) =>
  R.equals(MICRODATA, getViewer(state)),
);

export const getDataquery = memoize((state) => {
  const dimensionId = getDimensionId(state);
  const dimensions = getDimensions(state);
  const constraints = getMicrodataConstraints(state);
  const hierarchies = getHierarchies(state);

  const parsedConstraints = R.pipe(
    R.toPairs,
    R.reduce((memo, [dimId, valId]) => {
      if (R.has(dimId, hierarchies)) {
        const descendants = getDescendantsFromHCL(
          valId,
          R.prop(dimId, hierarchies),
        );
        return R.assoc(dimId, descendants, memo);
      }
      const dimValues = R.pipe(
        R.find(R.propEq(dimId, 'id')),
        R.prop('values'),
      )(dimensions);
      return R.assoc(dimId, getDescendants(valId, dimValues), memo);
    }, {}),
    R.assoc(dimensionId, ['DD']),
  )(constraints);

  return getDataqueryMicrodata(dimensions, parsedConstraints);
});

const getMicrodataRequestParams = memoize((state) => {
  const defaultParams = getDataRequestParams(state);
  const constraints = getMicrodataConstraints(state);
  const timeArtefact = getTimePeriodArtefact(state);
  const timeConstraint = R.prop(R.prop('id', timeArtefact), constraints);

  if (R.isNil(timeConstraint)) return defaultParams;
  return { startPeriod: timeConstraint, endPeriod: timeConstraint };
});

const getRawMicrodataRequestArgs = memoize((state) => {
  const a = getRawStructureRequestArgs(state);
  const b = getDataquery(state);
  const c = getMicrodataRequestParams(state);
  const d = getExternalReference(state);
  const e = [0, dataRequestSize - 1];
  return rawDataRequestArgsWrapper(a, b, c, d, e);
});

export const getMicrodataRequestArgs = memoize((state) => {
  const args = getRawMicrodataRequestArgs(state);
  const resultArgs = getRequestArgs({ ...args, type: 'data' });
  return R.assoc(
    'datasourceId',
    R.path(['datasource', 'id'], args),
    resultArgs,
  );
});

export const getMicrodataFileRequestArgs = (isFull) =>
  memoize((state) => {
    const requestArgs = getRawMicrodataRequestArgs(state);
    const display = getDisplay(state);
    return dataFileRequestArgsWrapper(isFull)(requestArgs, display);
  });

const getDataDimensions = memoize((state) =>
  R.pathOr([], ['structure', 'dimensions', 'observation'], getData(state)),
);

const getDataAttributes = memoize((state) =>
  R.pathOr([], ['structure', 'attributes', 'observation'], getData(state)),
);

const getParsedAttributes = memoize((state) => {
  const attributes = getDataAttributes(state);
  const dimensions = getDataDimensions(state);
  const customAttributes = getCustomAttributes(state);
  return rules2.parseAttributes(attributes, dimensions, customAttributes);
});

const getOneValueDimensions = memoize((state) => {
  const dimensions = getDataDimensions(state);
  const attributes = getParsedAttributes(state);
  return rules2.getOneValueDimensions(dimensions, attributes);
});

const getParsedCombinations = memoize((state) => {
  const annotations = getAnnotations(state);
  const locale = getLocale(state);
  const parsedAttributes = getParsedAttributes(state);
  const dimensions = getDataDimensions(state);
  const combinations = R.isEmpty(annotations)
    ? getDefaultCombinations(locale)
    : rules2.getCombinationDefinitions(annotations, locale);

  return rules2.parseCombinations(combinations, parsedAttributes, dimensions);
});

const getSeriesCombinations = memoize((state) => {
  const combinations = getParsedCombinations(state);
  const oneValueDimensions = getOneValueDimensions(state);
  return rules2.getSeriesCombinations(combinations, oneValueDimensions);
});

const getRefinedAttributes = memoize((state) => {
  const parsedAttributes = getParsedAttributes(state);
  const seriesCombinations = getSeriesCombinations(state);
  return rules2.refineAttributes(parsedAttributes, seriesCombinations);
});

const getDataflowAttributes = memoize((state) => {
  const attributes = getParsedAttributes(state);
  const combinations = getParsedCombinations(state);
  return rules2.getDataflowAttributes(attributes, combinations);
});

const getHeaderTitle = memoize((state) => {
  const dataflow = getDataflow(state);
  const name = getDataflowName(state);
  const attributes = getDataflowAttributes(state);
  const display = getDisplay(state);
  const customAttributes = getCustomAttributes(state);
  return rules2.getHeaderTitle(
    { id: dataflow.dataflowId, name },
    attributes,
    display,
    customAttributes,
  );
});

const getHeaderSubtitle = memoize((state) => {
  const dimensions = getOneValueDimensions(state);
  const combinations = getParsedCombinations(state);
  const customAttributes = getCustomAttributes(state);
  const display = getDisplay(state);
  const microdataDimension = getDimensionId(state);
  return rules2.getHeaderSubtitle(
    dimensions,
    combinations,
    customAttributes,
    display,
    microdataDimension,
  );
});

const getHeaderCombinations = memoize((state) => {
  const combinations = getParsedCombinations(state);
  const dimensions = getOneValueDimensions(state);
  const attributes = getRefinedAttributes(state);
  const display = getDisplay(state);
  return rules2.getHeaderCombinations(
    combinations,
    dimensions,
    attributes,
    display,
  );
});

export const getHeaderProps = memoize((state) => {
  return {
    title: getHeaderTitle(state),
    subtitle: getHeaderSubtitle(state),
    combinations: getHeaderCombinations(state),
  };
});
